import { t } from 'i18next';
import { useMutation, useQuery } from 'react-query';

import { contactService } from 'contacts/services';
import {
  IContact,
  IContactDetailsRequestPayload,
  IContactListRequestPayload,
  IContactSaveRequestPayload,
} from 'contacts/types';
import { IContactDetails } from 'contacts/types';
import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery } from 'core/hooks';
import { GetOneResponse, GetOneResult, IInfiniteQueryOptions } from 'core/types';

export const useGetContacts = (
  requestPayload?: IContactListRequestPayload,
  options: IInfiniteQueryOptions<IContact> = {
    fetchAllPages: true,
  }
) => useInfiniteQuery<IContact>(contactService.getContacts, 'contacts', requestPayload, options);

export const useGetContactDetails = (
  criteria: IContactDetailsRequestPayload,
  options: {
    enabled?: boolean;
    onSuccess?: (data: GetOneResult<IContactDetails>) => void;
  } = {}
): GetOneResult<IContactDetails> => {
  const { data, isLoading, isFetching, isError, refetch } = useQuery(
    ['contactDetails', criteria],
    ({ signal }) => contactService.getContactDetails(criteria, signal),
    {
      keepPreviousData: false,
      enabled: options.enabled,
      onSuccess: options.onSuccess,
    }
  );

  return {
    record: (data as unknown as GetOneResponse<IContactDetails>)?.record ?? null,
    isLoading,
    isFetching,
    isError,
    refetch,
  };
};

export const useContactActions = () => ({
  ...useSaveContact(),
  ...useSendInvitation(),
  ...useRevokeInvitation(),
  ...useRemoveContacts(),
  ...useRestoreContacts(),
  ...useUpdateContactOwner(),
  ...useUpdateContactGroups(),
});

const useSaveContact = () => {
  const { errors } = useHandleErrors({
    success: ({ contact }) =>
      contact?.id
        ? { message: t('messageContactUpdateSuccess') }
        : { message: t('messageContactCreateSuccess') },
    loading: ({ contact }) =>
      contact?.id
        ? { message: t('messageContactUpdateInProgress') }
        : { message: t('messageContactCreateInProgress') },
    error: ({ contact }) =>
      contact?.id
        ? { title: t('errorTitleContactUpdate') }
        : { title: t('errorTitleContactCreate') },
    invalidateQueries: ['contacts'],
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(
    (data: IContactSaveRequestPayload) => contactService.saveContact(data),
    {
      ...errors,
    }
  );

  return { saveContact: mutateAsync };
};

const useSendInvitation = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageInvitationSentSuccess') },
    loading: { message: t('messageInvitationSendInProgress') },
    error: { title: t('errorTitleInvitationSend') },
    invalidateQueries: ['contacts'],
    position: NotificationPosition.FORM,
  });

  const { mutateAsync, isLoading } = useMutation(
    (contactId: number) => contactService.sendInvitation(contactId),
    {
      ...errors,
    }
  );

  return { sendInvitation: mutateAsync, isSendInvitationLoading: isLoading };
};

const useRevokeInvitation = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageInvitationRevokedSuccess') },
    loading: { message: t('messageInvitationRevokeInProgress') },
    error: { title: t('errorTitleInvitationRevoke') },
    invalidateQueries: ['contacts'],
    position: NotificationPosition.FORM,
  });

  const { mutateAsync, isLoading } = useMutation(
    (contactId: number) => contactService.revokeInvitation(contactId),
    {
      ...errors,
    }
  );

  return { revokeInvitation: mutateAsync, isRevokeInvitationLoading: isLoading };
};

const useRemoveContacts = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageContactRemoveSuccess') },
    loading: { message: t('messageContactRemoveInProgress') },
    error: { title: t('errorTitleContactRemove') },
    invalidateQueries: ['contacts'],
    position: NotificationPosition.TOP,
  });

  const { mutateAsync, isLoading } = useMutation(
    (contactIds: number[]) => contactService.removeContacts(contactIds),
    {
      ...errors,
    }
  );

  return { removeContacts: mutateAsync, isRemoveContactsLoading: isLoading };
};

const useRestoreContacts = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageContactRestoreSuccess') },
    loading: { message: t('messageContactRestoreInProgress') },
    error: { title: t('errorTitleContactRestore') },
    invalidateQueries: ['contacts'],
    position: NotificationPosition.TOP,
  });

  const { mutateAsync, isLoading } = useMutation(
    (contactIds: number[]) => contactService.restoreContacts(contactIds),
    {
      ...errors,
    }
  );

  return { restoreContacts: mutateAsync, isRestoreContactsLoading: isLoading };
};

const useUpdateContactOwner = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageContactOwnerUpdateSuccess') },
    loading: { message: t('messageContactOwnerUpdateInProgress') },
    error: { title: t('errorTitleContactOwnerUpdate') },
    invalidateQueries: ['contacts'],
    position: NotificationPosition.TOP,
  });

  const { mutateAsync, isLoading } = useMutation(
    ({
      corporateUserId,
      contactIds,
      contactGroupIds,
    }: {
      corporateUserId: number;
      contactIds: number[];
      contactGroupIds?: number[];
    }) => contactService.updateContactOwner(corporateUserId, contactIds, contactGroupIds),
    {
      ...errors,
    }
  );

  return { updateContactOwner: mutateAsync, isUpdateContactOwnerLoading: isLoading };
};

const useUpdateContactGroups = () => {
  const { errors } = useHandleErrors({
    success: { message: t('messageContactGroupUpdateSuccess') },
    loading: { message: t('messageContactGroupUpdateInProgress') },
    error: { title: t('errorTitleContactGroupUpdate') },
    invalidateQueries: ['contacts'],
    position: NotificationPosition.TOP,
  });

  const { mutateAsync, isLoading } = useMutation(
    (contacts: Array<{ id: number; contactGroupingId: number }>) =>
      contactService.upsertContacts(contacts),
    {
      ...errors,
    }
  );

  return { updateContactGroups: mutateAsync, isUpdateContactGroupsLoading: isLoading };
};
